import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useToast } from '../components/Toast';
import { getReferralFromUrl, isValidAddress, parseUSDT } from '../utils/helpers';
import { DEFAULT_REFERRER } from '../config/contract';
import './Register.css';

const Register = () => {
  const {
    account,
    planContract,
    usdtContract,
    usdtBalance,
    isConnecting,
    connectWallet,
    refreshUserData,
    setIsRegistered
  } = useWeb3();
  const toast = useToast();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [sponsorAddress, setSponsorAddress] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('50');
  const [autoMode, setAutoMode] = useState(false); // Default to Normal mode
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');

  // Get referral from URL or sessionStorage on mount and when location changes
  useEffect(() => {
    // First check URL params directly from location
    const urlParams = new URLSearchParams(location.search);
    let ref = urlParams.get('ref');
    console.log('Register - URL param ref:', ref);
    
    // If not in URL, try sessionStorage
    if (!ref) {
      ref = sessionStorage.getItem('referralAddress');
      console.log('Register - sessionStorage ref:', ref);
    }
    
    // If still not found, try the helper function
    if (!ref) {
      ref = getReferralFromUrl();
      console.log('Register - getReferralFromUrl ref:', ref);
    }
    
    // Set sponsor if valid
    if (ref && isValidAddress(ref)) {
      setSponsorAddress(ref);
      console.log('Register - Valid sponsor set:', ref);
    }
  }, [location.search]);

  const handleRegister = async () => {
    if (!account) {
      await connectWallet();
      return;
    }

    if (parseFloat(purchaseAmount) < 20) {
      toast.error('Minimum purchase is $20');
      setError('Minimum purchase is $20');
      return;
    }

    if (parseFloat(usdtBalance) < parseFloat(purchaseAmount)) {
      toast.error('Insufficient USDT balance');
      setError('Insufficient USDT balance');
      return;
    }

    setIsRegistering(true);
    setError('');

    try {
      const amount = parseUSDT(purchaseAmount);
      const sponsor = sponsorAddress && isValidAddress(sponsorAddress) 
        ? sponsorAddress 
        : DEFAULT_REFERRER || ethers.constants.AddressZero;

      // Check allowance
      const allowance = await usdtContract.allowance(account, planContract.address);

      if (allowance.lt(amount)) {
        toast.info('Approving USDT...');
        const approveTx = await usdtContract.approve(
          planContract.address,
          ethers.constants.MaxUint256
        );
        await approveTx.wait();
        toast.success('USDT approved!');
      }

      // Purchase NFTs (this registers the user)
      // Contract now takes (amount, sponsor, autoMode, normalMode).
      // Auto and Normal are mutually exclusive — normalMode = !autoMode.
      toast.info('Registering...');
      const tx = await planContract.purchaseNFTs(amount, sponsor, autoMode, !autoMode);
      await tx.wait();

      toast.success('Registration successful!');
      await refreshUserData();
      setIsRegistered(true);

    } catch (err) {
      console.error('Registration error:', err);
      const errorMsg = err.reason || 'Transaction failed. Please try again.';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="register-page">
      {/* Background */}
      <div className="bg-universe">
        <div className="bg-grid"></div>
        <div className="bg-orb bg-orb-1"></div>
        <div className="bg-orb bg-orb-2"></div>
        <div className="bg-orb bg-orb-3"></div>
        <div className="scan-line"></div>
        <div className="particles">
          {[...Array(20)].map((_, i) => (
            <span key={`particle-${i}`} className="particle" style={{ '--i': i }}></span>
          ))}
        </div>
      </div>

      {/* Logo */}
      <div className="register-logo animate-fade-up">
        <img src="/logo.gif" alt="Adrenaline" className="logo-img" />
        <div className="logo-text">
          <span className="logo-title">ADRENALINE NFT</span>
          <span className="logo-sub">NFT PLATFORM</span>
        </div>
      </div>

      {/* Registration Card */}
      <div className="register-card glass-card animate-fade-up delay-1">
        <div className="card-header">
          <h1>Join Adrenaline NFT</h1>
          <p>Start your NFT investment journey</p>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          {[1, 2, 3].map((s) => (
            <div key={`step-${s}`} className={`step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
              <span className="step-num">{s}</span>
              <span className="step-label">
                {s === 1 ? 'Sponsor' : s === 2 ? 'Amount' : 'Mode'}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Sponsor */}
        {step === 1 && (
          <div className="step-content animate-fade-up">
            <h2>Enter Sponsor Address</h2>
            <p className="step-desc">Enter your referrer's wallet address (optional)</p>
            
            <div className="input-group">
              <input
                type="text"
                className="cyber-input"
                placeholder="0x... (leave empty for default)"
                value={sponsorAddress}
                onChange={(e) => setSponsorAddress(e.target.value)}
              />
            </div>

            {!account ? (
              <button 
                className="btn btn-primary btn-full"
                onClick={connectWallet}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </button>
            ) : (
              <button 
                className="btn btn-primary btn-full"
                onClick={() => setStep(2)}
              >
                Continue
              </button>
            )}
          </div>
        )}

        {/* Step 2: Amount */}
        {step === 2 && (
          <div className="step-content animate-fade-up">
            <h2>Investment Amount</h2>
            <p className="step-desc">Choose your initial investment (Min: $20)</p>

            <div className="amount-quick-btns">
              {[20, 50, 100, 200, 500, 1000].map((amt) => (
                <button
                  key={`amt-${amt}`}
                  className={`amount-btn ${purchaseAmount === amt.toString() ? 'active' : ''}`}
                  onClick={() => setPurchaseAmount(amt.toString())}
                >
                  ${amt}
                </button>
              ))}
            </div>

            <div className="input-group">
              <input
                type="number"
                className="cyber-input"
                placeholder="Enter amount"
                value={purchaseAmount}
                onChange={(e) => setPurchaseAmount(e.target.value)}
                min="20"
              />
              <span className="input-suffix">USDT</span>
            </div>

            <div className="balance-info">
              <span>Your Balance:</span>
              <span className="font-numbers">{parseFloat(usdtBalance).toFixed(2)} USDT</span>
            </div>

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button 
                className="btn btn-primary"
                onClick={() => setStep(3)}
                disabled={parseFloat(purchaseAmount) < 20}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Mode Selection */}
        {step === 3 && (
          <div className="step-content animate-fade-up">
            <h2>Choose Your Mode</h2>
            <p className="step-desc">Select how you want to receive your earnings</p>

            <div className="mode-options">
              {/* Normal Mode - First and Default */}
              <div 
                className={`mode-card normal ${!autoMode ? 'selected' : ''}`}
                onClick={() => setAutoMode(false)}
              >
                <div className="mode-icon">💰</div>
                <h3>Normal Mode</h3>
                <div className="mode-features">
                  <div className="feature">
                    <span className="feature-icon">💵</span>
                    <span className="feature-text">0.2% NFT Profit</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🏦</span>
                    <span className="feature-text">No Ceiling Limit</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">💳</span>
                    <span className="feature-text">Instant principal return</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🎯</span>
                    <span className="feature-text">Direct to wallet</span>
                  </div>
                </div>
                {!autoMode && <div className="mode-check">✓</div>}
              </div>

              {/* Auto Mode - Second */}
              <div 
                className={`mode-card auto ${autoMode ? 'selected' : ''}`}
                onClick={() => setAutoMode(true)}
              >
                <div className="mode-badge recommended">RECOMMENDED</div>
                <div className="mode-icon">🚀</div>
                <h3>Auto Mode</h3>
                <div className="mode-features">
                  <div className="feature highlight">
                    <span className="feature-icon">💎</span>
                    <span className="feature-text">0.5% NFT Profit</span>
                  </div>
                  <div className="feature highlight">
                    <span className="feature-icon">📈</span>
                    <span className="feature-text">2X (200%) Total Ceiling</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🔄</span>
                    <span className="feature-text">Auto-reinvest principal</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">⚡</span>
                    <span className="feature-text">Compound your earnings</span>
                  </div>
                </div>
                {autoMode && <div className="mode-check">✓</div>}
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
              <button 
                className="btn btn-primary btn-register"
                onClick={handleRegister}
                disabled={isRegistering}
              >
                {isRegistering ? 'Processing...' : `Register & Invest $${purchaseAmount}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="register-note animate-fade-up delay-2">
        <p>💡 0.1% Direct Referral Bonus • 30 Level Matrix Income</p>
      </div>
    </div>
  );
};

export default Register;